import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, AlertTriangle, X, CheckCircle2, Shield } from "lucide-react";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY_COLOR = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-amber-100 text-amber-800",
  high: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const IncidentLogPage = () => {
  const { user } = useAuth() || {};
  const isSupervisorPlus = ["supervisor", "manager", "admin", "office_staff", "super_admin"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resolveFor, setResolveFor] = useState(null);
  const [resolution, setResolution] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [form, setForm] = useState({
    incident_type: "incident",
    severity: "medium",
    title: "",
    description: "",
    location: "",
    job_id: "",
    machine_id: "",
    immediate_action: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const [r, m, j] = await Promise.all([
        axios.get(`${API}/mcs/incidents${params}`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/jobs?status=active`),
      ]);
      setItems(r.data || []);
      setMachines(m.data || []);
      setJobs(j.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Title and description required");
    setSaving(true);
    try {
      await axios.post(`${API}/mcs/incidents`, form);
      toast.success("Incident logged");
      setShowCreate(false);
      setForm({ incident_type: "incident", severity: "medium", title: "", description: "", location: "", job_id: "", machine_id: "", immediate_action: "" });
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const submitResolve = async () => {
    if (!resolution.trim()) return toast.error("Resolution notes required");
    try {
      await axios.post(`${API}/mcs/incidents/${resolveFor}/resolve`, { resolution });
      toast.success("Resolved");
      setResolveFor(null);
      setResolution("");
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="incident-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900 font-heading">Incident & Fault Log</h1>
          <p className="text-sm text-slate-500 mt-1">Record incidents, near misses, and faults. High/critical alerts go straight to admins.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-md font-semibold" data-testid="create-incident-btn">
          <Plus className="w-5 h-5" /> Report Incident
        </button>
      </div>

      <div className="flex gap-2">
        {["", "open", "resolved"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase rounded ${filterStatus === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`filter-${s || "all"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-md text-slate-400">No incidents logged.</div>
      ) : (
        <div className="space-y-3">
          {items.map(it => (
            <div key={it.id} className="bg-white border-2 border-slate-200 rounded-md p-4" data-testid={`incident-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${SEVERITY_COLOR[it.severity] || "bg-slate-100"}`}>{it.severity}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-700">{it.incident_type.replace("_", " ")}</span>
                  {it.status === "resolved" ? (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">Resolved</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-800">Open</span>
                  )}
                </div>
                {it.severity === "critical" && <AlertTriangle className="w-5 h-5 text-red-500" />}
              </div>
              <div className="font-bold text-slate-900">{it.title}</div>
              <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{it.description}</div>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                <span>By {it.reported_by_name}</span>
                {it.location && <span>· {it.location}</span>}
                <span>· {new Date(it.occurred_at).toLocaleString()}</span>
              </div>
              {it.immediate_action && (
                <div className="text-xs text-slate-700 mt-2 bg-slate-50 rounded p-2">
                  <span className="font-semibold uppercase text-slate-500">Immediate action:</span> {it.immediate_action}
                </div>
              )}
              {it.resolution && (
                <div className="text-xs text-emerald-800 mt-2 bg-emerald-50 rounded p-2">
                  <span className="font-semibold uppercase">Resolution by {it.resolved_by_name}:</span> {it.resolution}
                </div>
              )}
              {isSupervisorPlus && it.status === "open" && (
                <button onClick={() => { setResolveFor(it.id); setResolution(""); }} className="mt-3 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-semibold inline-flex items-center gap-1" data-testid={`resolve-${it.id}`}>
                  <CheckCircle2 className="w-4 h-4" /> Resolve
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                <h2 className="text-xl font-bold">Report Incident / Fault</h2>
              </div>
              <button onClick={() => setShowCreate(false)}><X className="w-6 h-6 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <select value={form.incident_type} onChange={(e) => setForm({...form, incident_type: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="inc-type">
                  <option value="incident">Incident</option>
                  <option value="near_miss">Near miss</option>
                  <option value="fault">Fault</option>
                </select>
                <select value={form.severity} onChange={(e) => setForm({...form, severity: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="inc-severity">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <input placeholder="Short title (e.g. Conveyor belt jam)" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="inc-title" />
              <textarea placeholder="What happened? Be specific." value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={4} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="inc-description" />
              <input placeholder="Location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.job_id} onChange={(e) => setForm({...form, job_id: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2">
                  <option value="">-- Job --</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                </select>
                <select value={form.machine_id} onChange={(e) => setForm({...form, machine_id: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2">
                  <option value="">-- Machine --</option>
                  {machines.map(m => <option key={m.id} value={m.id}>{m.rego || m.name}</option>)}
                </select>
              </div>
              <textarea placeholder="Immediate action taken…" value={form.immediate_action} onChange={(e) => setForm({...form, immediate_action: e.target.value})} rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-5 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreate(false)} disabled={saving} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-md font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md font-semibold" data-testid="inc-submit">
                {saving ? "Submitting…" : "Submit Incident"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resolveFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-md">
            <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between">
              <h2 className="text-xl font-bold">Resolve Incident</h2>
              <button onClick={() => setResolveFor(null)}><X className="w-6 h-6 text-slate-400" /></button>
            </div>
            <div className="p-5">
              <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={5} placeholder="How was it resolved? What was done to prevent recurrence?" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="inc-resolution" />
            </div>
            <div className="border-t-2 border-slate-200 p-5 flex gap-3">
              <button onClick={() => setResolveFor(null)} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-md font-semibold">Cancel</button>
              <button onClick={submitResolve} className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold" data-testid="inc-resolve-submit">
                Mark Resolved
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentLogPage;
